import { type FormEvent, useState } from "react";
import { ApiError, api } from "./api";
import { Icon } from "./icons";

function Brand() {
  return (
    <div className="brand">
      <Icon name="logo" />
      <span>Central Hub</span>
    </div>
  );
}

export function SignIn({
  oidcEnabled,
  expired,
  error,
  onOidc,
  onToken,
}: {
  oidcEnabled: boolean;
  expired: boolean;
  error: string | null;
  onOidc(): void;
  onToken(token: string): void;
}) {
  const [token, setToken] = useState("");
  const [checking, setChecking] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(!oidcEnabled);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setChecking(true);
    setTokenError(null);
    try {
      await api.me(token.trim());
      onToken(token.trim());
    } catch (e) {
      setTokenError(
        e instanceof ApiError && e.status === 401
          ? "That token isn't valid."
          : "Couldn't reach the hub.",
      );
    } finally {
      setChecking(false);
    }
  };

  return (
    <main className="center">
      <section className="signin" aria-labelledby="signin-title">
        <Brand />
        {expired && (
          <div className="notice" data-status="degraded" role="status">
            <Icon name="clock" />
            <div>
              <p>Your session expired</p>
              <p>
                Sign in again to resume the live view. Monitoring kept running while you were away.
              </p>
            </div>
          </div>
        )}
        {error && (
          <div className="notice" data-status="down" role="alert">
            <Icon name="down" />
            <div>
              <p>Sign-in failed</p>
              <p>{error}</p>
            </div>
          </div>
        )}
        <div>
          <h1 id="signin-title">Sign in</h1>
          <p className="lead">
            {oidcEnabled
              ? "Use your account to open the status panel."
              : "Paste an API token from the hub’s configuration."}
          </p>
        </div>
        {oidcEnabled && (
          <>
            <button type="button" className="primary" onClick={onOidc}>
              Continue with single sign-on
              <Icon name="arrow" />
            </button>
            <p className="fine">
              You’ll be redirected to your identity provider (OpenID Connect) and brought back here.
            </p>
          </>
        )}
        {oidcEnabled && !showToken && (
          <button type="button" className="linkbtn" onClick={() => setShowToken(true)}>
            Use an API token instead
          </button>
        )}
        {showToken && (
          <form onSubmit={submit} className="signin-form">
            {oidcEnabled && <p className="divider">or</p>}
            <div className="field">
              <label htmlFor="token">API token</label>
              <input
                id="token"
                type="password"
                autoComplete="off"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                required
                minLength={32}
              />
            </div>
            {tokenError && (
              <p role="alert" className="summary-warn">
                {tokenError}
              </p>
            )}
            <button
              type="submit"
              className="primary"
              disabled={checking || token.trim().length < 32}
            >
              {checking ? "Checking…" : "Sign in with token"}
            </button>
          </form>
        )}
      </section>
    </main>
  );
}

export function Forbidden({
  details,
  onSignOut,
}: {
  details: Record<string, unknown>;
  onSignOut(): void;
}) {
  const sub = typeof details.sub === "string" ? details.sub : null;
  const email = typeof details.email === "string" ? details.email : null;
  // Anyone the identity provider accepts can get here: say nothing about the hub's setup.
  return (
    <main className="center">
      <section className="signin" aria-labelledby="forbidden-title">
        <Brand />
        <div>
          <h1 id="forbidden-title">No access</h1>
          <p className="lead">
            You signed in as <b>{email ?? sub ?? "an unknown user"}</b>, but this account has no
            access to this hub.
          </p>
        </div>
        <div className="notice" data-status="unknown">
          <Icon name="lock" />
          <div>
            <p>If you should have access, ask the hub’s administrator.</p>
          </div>
        </div>
        <button type="button" className="primary" onClick={onSignOut}>
          Sign out
        </button>
      </section>
    </main>
  );
}

export function Unreachable({ error }: { error: string }) {
  return (
    <main className="center">
      <section className="signin" aria-labelledby="unreachable-title">
        <Brand />
        <div className="notice" data-status="down" role="alert">
          <Icon name="down" className="alarm" />
          <div>
            <p id="unreachable-title">Can’t reach the hub</p>
            <p>{error}. Retrying every 10 seconds.</p>
          </div>
        </div>
      </section>
    </main>
  );
}

export function Loading() {
  return (
    <main className="loading" aria-busy="true">
      <div className="loading-head" role="status">
        <span className="ring pulse" />
        <div>
          <p>Connecting to the hub…</p>
          <p className="muted">Fetching the latest status for every target.</p>
        </div>
      </div>
      <div className="grid">
        {Array.from({ length: 12 }, (_, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: a fixed, static skeleton
            key={i}
            className="skel"
            aria-hidden="true"
            style={{ height: i % 4 === 0 ? "9.5rem" : "4rem" }}
          />
        ))}
      </div>
    </main>
  );
}
